import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Wallet, CreditCard, Printer, Ban, Search, Volume2, VolumeX, Wifi, WifiOff,
  Store, MapPin, Phone, Clock, ChevronRight, HandCoins, UserX, AlertTriangle, Smartphone,
} from 'lucide-react';
import { fmtMoney, fmtTimeAgo, useRealtime, usePolling } from '@/lib/realtime';
import { ORDER_STATUS_COLORS, getContextualStatusLabel } from '@/lib/orderStatus';
import { playAdminPing, unlockAudio, isAudioUnlocked } from '@/lib/chime';
import { OrderDetailDialog } from './OrderDetailDialog';

const ACTIVE_STATUSES = ['received', 'preparing', 'ready', 'out_for_delivery', 'pending_payment'];

// "Active" for the cashier queue includes anything not yet paid AND anything where
// a driver is still holding cash that hasn't been remitted to the cashier yet.
function isQueueActive(o) {
  if (ACTIVE_STATUSES.includes(o.status)) return true;
  if (o.payment_status !== 'paid') return true;
  if (o.driver_cash_held && !o.driver_cash_remitted) return true;
  return false;
}

/**
 * Same-calendar-day check against the browser's local clock. Used by the POS
 * "Current day" filter so the cashier sees only today's queue by default —
 * critical once the branch has accumulated thousands of historical orders.
 */
function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

// Helper composition keeps the channel filter table below readable.
const notTerminal = (o) => !['cancelled', 'overdue'].includes(o.status);
const todayActive = (o) => notTerminal(o) && isToday(o.created_at);

const CHANNEL_FILTERS = [
  // DEFAULT — today's working queue. Surfaces orders created on the current
  // calendar day that are not yet cancelled/overdue. This keeps the POS fast
  // and focused even after the branch has accumulated thousands of historical
  // orders.
  { key: 'today', label: 'Current day', match: todayActive },
  { key: 'dine_in', label: 'Dine-in (iPad)', match: (o) => o.order_type === 'dine_in' && todayActive(o) },
  { key: 'pickup', label: 'Pickup', match: (o) => o.order_type === 'pickup' && todayActive(o) },
  { key: 'delivery', label: 'Delivery', match: (o) => o.order_type === 'delivery' && todayActive(o) },
  { key: 'unpaid', label: 'Unpaid only', match: (o) => o.payment_status !== 'paid' && todayActive(o) },
  {
    key: 'driver_cash',
    label: 'Cash with driver',
    match: (o) => o.driver_cash_held && !o.driver_cash_remitted && todayActive(o),
  },
  {
    // Recent cancellations — surfaces orders that were cancelled (by POS,
    // by the customer, or by the no-show auto-policy) so the cashier can
    // audit, reprint, or follow up. Cancelled orders are NOT counted in
    // the other channel filters above.
    key: 'cancelled',
    label: 'Cancelled orders',
    match: (o) => o.status === 'cancelled',
  },
  {
    // Orders that crossed the kitchen day-reset boundary while still
    // unfinished. They are auto-flipped to status='overdue' by the
    // backend so the kitchen lanes clear each morning. Surfaced here so
    // a manager can audit them (refund? remake? was the printer offline?).
    key: 'overdue',
    label: 'Overdue (yesterday)',
    match: (o) => o.status === 'overdue',
  },
  // ESCAPE HATCH — unscoped view across all dates / channels. Used when the
  // cashier needs to look up a historical order; also the implicit scope used
  // by the search bar (see filtering logic below).
  { key: 'all', label: 'All channels', match: notTerminal },
];

const ORDER_TYPE_BADGE = {
  dine_in: { label: 'Dine-in', cls: 'bg-blue-100 text-blue-900 border-blue-200' },
  pickup: { label: 'Pickup', cls: 'bg-purple-100 text-purple-900 border-purple-200' },
  delivery: { label: 'Delivery', cls: 'bg-amber-100 text-amber-900 border-amber-200' },
};

import { getCurrentPolicy } from '@/lib/policy';

// ---- Payment formatting helpers --------------------------------------------
// The backend stores payment_method as one of: cash | card | mtn_money |
// orange_money. The POS UI also exposes a fourth user-facing choice ("cod" -
// pay on delivery) which is mapped down to backend `cash` + payment_status=unpaid.
// To make the printed receipt/ticket unambiguous for the kitchen, driver and
// customer we always print a human-readable line.
const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  card: 'Card',
  mtn_money: 'Mobile Money (MTN)',
  orange_money: 'Mobile Money (Orange)',
  mobile_money: 'Mobile Money',
  cod: 'Pay on Delivery (COD)',
};

const PAYMENT_STATUS_LABELS = {
  paid: 'PAID',
  unpaid: 'UNPAID',
  pending: 'PENDING',
};

/**
 * Build a human-readable payment line:
 *  - "PAID — Mobile Money (MTN)"
 *  - "UNPAID — Pay on Delivery (COD)"
 *  - "UNPAID — Cash"
 * Detects COD heuristically: delivery + cash + unpaid → treat as COD.
 */
function formatPaymentLine(order) {
  const status = (order.payment_status || 'unpaid').toLowerCase();
  let method = (order.payment_method || 'cash').toLowerCase();
  // Heuristic for COD: backend stores cash+unpaid on delivery orders where
  // the driver collects at the door. Show this explicitly so the kitchen +
  // driver know cash is OWED at hand-off (vs prepaid cash at counter).
  if (order.order_type === 'delivery' && method === 'cash' && status !== 'paid') {
    method = 'cod';
  }
  const methodLabel = PAYMENT_METHOD_LABELS[method] || method.toUpperCase();
  const statusLabel = PAYMENT_STATUS_LABELS[status] || status.toUpperCase();
  return `${statusLabel} \u2014 ${methodLabel}`;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[<>&"]/g, (m) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[m]);
}

function buildReceiptHtml(order) {
  const policy = getCurrentPolicy();
  const showTax = !!policy.tax_show_on_receipt;
  const showTip = !!policy.tip_show_on_receipt;
  const showTipSugg = !!policy.tip_suggestions_enabled;
  const tipSuggestions = Array.isArray(policy.tip_suggestions) ? policy.tip_suggestions : [];
  const items = (order.items || []).map((it) => `
    <tr><td>${it.quantity}× ${it.name}</td><td style="text-align:right">${fmtMoney(it.line_total, order.currency)}</td></tr>
  `).join('');
  const channelLine =
    order.order_type === 'dine_in' ? `Table ${order.table_number ?? '—'}`
    : order.order_type === 'pickup' ? 'Pickup'
    : order.order_type === 'delivery' ? 'Delivery' : '';

  // Type-specific block printed under the order metadata so the cashier (and
  // the customer reading the receipt) always sees the right context:
  //   dine-in   → table number
  //   pickup    → customer name + phone
  //   delivery  → delivery address + phone
  const addr = order.delivery_address || {};
  const channelBlockHtml = (() => {
    if (order.order_type === 'dine_in') {
      return `<p class="meta"><strong>Table:</strong> ${order.table_number ?? '—'}${order.customer_name ? ` · ${order.customer_name}` : ''}</p>`;
    }
    if (order.order_type === 'pickup') {
      return `<p class="meta"><strong>Pickup for:</strong> ${order.customer_name || 'Guest'}${order.customer_phone ? ` · ${order.customer_phone}` : ''}</p>`;
    }
    if (order.order_type === 'delivery') {
      const lines = [addr.line1, addr.line2, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')].filter(Boolean).join('<br/>');
      return `
        <p class="meta"><strong>Deliver to:</strong> ${order.customer_name || 'Guest'}${order.customer_phone ? ` · ${order.customer_phone}` : ''}</p>
        <p class="meta">${lines || '—'}</p>
        ${addr.notes ? `<p class="meta"><em>Notes: ${String(addr.notes).replace(/[<>&]/g, (m) => ({'<':'&lt;','>':'&gt;','&':'&amp;'})[m])}</em></p>` : ''}
        ${order.verification_code ? `<p class="meta"><strong>Verification PIN:</strong> ${order.verification_code}</p>` : ''}
      `;
    }
    return '';
  })();
  const subtotalForTips = Number(order.subtotal) || 0;
  const tipSuggestionsHtml = (showTipSugg && tipSuggestions.length > 0) ? `
    <hr/>
    <p class="muted" style="margin:4px 0">Suggested tips</p>
    <div class="tips">
      ${tipSuggestions.map((pct) => `
        <div class="tip">
          <div class="pct">${pct}%</div>
          <div class="amt">${fmtMoney((subtotalForTips * pct) / 100, order.currency)}</div>
        </div>
      `).join('')}
    </div>
  ` : '';
  return `<!doctype html><html><head><title>Receipt #${order.order_number}</title>
    <style>
      body{font-family:ui-monospace,SFMono-Regular,monospace;max-width:320px;margin:12px auto;color:#111}
      h1{font-size:18px;margin:0 0 4px;text-align:center}
      .muted{color:#555;font-size:12px;text-align:center}
      .meta{margin:4px 0;font-size:12px;text-align:left;color:#222}
      table{width:100%;border-collapse:collapse;margin-top:12px;font-size:13px}
      td{padding:2px 0;vertical-align:top}
      hr{border:none;border-top:1px dashed #999;margin:8px 0}
      .row{display:flex;justify-content:space-between;font-size:13px}
      .total{font-weight:700;font-size:15px}
      .tips{display:flex;justify-content:space-around;margin-top:4px;gap:4px}
      .tip{border:1px dashed #999;border-radius:4px;padding:4px 6px;text-align:center;flex:1}
      .tip .pct{font-weight:700;font-size:13px}
      .tip .amt{font-size:11px;color:#555;margin-top:1px}
      .actions{margin-top:16px;display:flex;gap:8px;justify-content:center}
      .actions button{padding:8px 16px;border:1px solid #c2410c;background:#c2410c;color:white;border-radius:6px;font-size:13px;cursor:pointer;font-family:inherit}
      .actions button.secondary{background:white;color:#111;border-color:#ccc}
      @media print { .actions { display:none } }
    </style></head><body>
    <h1>City Grill</h1>
    <p class="muted">Order #${order.order_number} · ${channelLine}</p>
    <p class="muted">${new Date(order.created_at).toLocaleString()}</p>
    ${channelBlockHtml}
    <hr/>
    <table>${items}</table>
    <hr/>
    <div class="row"><span>Subtotal</span><span>${fmtMoney(order.subtotal, order.currency)}</span></div>
    ${showTax && Number(order.tax) > 0 ? `<div class="row"><span>Tax</span><span>${fmtMoney(order.tax, order.currency)}</span></div>` : ''}
    ${order.delivery_fee > 0 ? `<div class="row"><span>Delivery</span><span>${fmtMoney(order.delivery_fee, order.currency)}</span></div>` : ''}
    ${showTip && Number(order.tip) > 0 ? `<div class="row"><span>Tip</span><span>${fmtMoney(order.tip, order.currency)}</span></div>` : ''}
    <div class="row total"><span>Total</span><span>${fmtMoney(order.total, order.currency)}</span></div>
    ${tipSuggestionsHtml}
    <hr/>
    <p class="meta" style="text-align:center;font-weight:700;font-size:13px;border:1px solid #000;padding:4px;border-radius:4px">
      PAYMENT: ${formatPaymentLine(order)}
    </p>
    <p class="muted">Thank you!</p>
    <div class="actions">
      <button onclick="window.print()">Print</button>
      <button class="secondary" onclick="window.close()">Close</button>
    </div>
    <script>
      // Auto-open print dialog after layout settles; close only AFTER the user
      // dismisses the dialog. If the browser blocks afterprint (some embeds),
      // the Close button remains visible so the cashier can dismiss manually.
      window.addEventListener('load', function () {
        window.addEventListener('afterprint', function () { window.close(); });
        setTimeout(function () { try { window.print(); } catch (e) {} }, 120);
      });
    </script>
  </body></html>`;
}

function buildKitchenTicketHtml(order) {
  // Minimal paper ticket attached to the food itself. The packer / runner uses
  // it to double-check items; the cashier matches the order ID before
  // releasing it. Phone is shown ONLY for delivery (privacy for dine-in/pickup).
  const items = (order.items || []).map((it) => `
    <tr>
      <td class="qty">${it.quantity}×</td>
      <td>${it.name}${it.special_instructions ? `<div class="note">→ ${String(it.special_instructions).replace(/[<>&]/g, (m) => ({'<':'&lt;','>':'&gt;','&':'&amp;'})[m])}</div>` : ''}</td>
    </tr>
  `).join('');
  const addr = order.delivery_address || {};
  const ot = order.order_type;
  const channelLabel = ot === 'dine_in' ? `Table ${order.table_number ?? '—'}`
    : ot === 'pickup' ? 'Pickup'
    : ot === 'delivery' ? 'Delivery' : '—';
  const channelBlock = (() => {
    if (ot === 'dine_in') {
      return `<p class="line"><b>Table:</b> ${order.table_number ?? '—'}${order.customer_name ? ` · ${order.customer_name}` : ''}</p>`;
    }
    if (ot === 'pickup') {
      return `<p class="line"><b>Pickup for:</b> ${order.customer_name || 'Guest'}</p>`;
    }
    if (ot === 'delivery') {
      const lines = [addr.line1, addr.line2, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')].filter(Boolean).join('<br/>');
      return `
        <p class="line"><b>Deliver to:</b> ${order.customer_name || 'Guest'}${order.customer_phone ? ` · ${order.customer_phone}` : ''}</p>
        <p class="line">${lines || '—'}</p>
        ${order.verification_code ? `<p class="line"><b>PIN:</b> ${order.verification_code}</p>` : ''}
      `;
    }
    return '';
  })();
  return `<!doctype html><html><head><title>Kitchen ticket #${order.order_number}</title>
    <style>
      body{font-family:ui-monospace,SFMono-Regular,monospace;max-width:320px;margin:12px auto;color:#000}
      .ribbon{background:#000;color:#fff;text-align:center;padding:4px;font-weight:700;letter-spacing:2px;text-transform:uppercase;font-size:13px;border-radius:4px}
      h1{font-size:22px;margin:6px 0 2px;text-align:center;font-family:ui-monospace,SFMono-Regular,monospace;letter-spacing:1px}
      .ord{font-size:30px;font-weight:800;text-align:center;margin:6px 0 2px;letter-spacing:1px}
      .meta{font-size:12px;text-align:center;color:#444;margin:0 0 8px}
      .line{margin:2px 0;font-size:13px}
      table{width:100%;border-collapse:collapse;margin:6px 0;font-size:14px}
      td{padding:4px 0;vertical-align:top;border-bottom:1px dashed #aaa}
      .qty{width:32px;font-weight:700}
      .note{font-size:11px;color:#444;font-style:italic}
      hr{border:none;border-top:1px dashed #999;margin:6px 0}
      .actions{margin-top:12px;display:flex;gap:8px;justify-content:center}
      .actions button{padding:6px 14px;border:1px solid #c2410c;background:#c2410c;color:white;border-radius:6px;font-size:13px;cursor:pointer;font-family:inherit}
      .actions button.secondary{background:white;color:#111;border-color:#ccc}
      @media print { .actions { display:none } }
    </style></head><body>
    <div class="ribbon">${channelLabel}</div>
    <div class="ord">#${order.order_number}</div>
    <p class="meta">${new Date(order.created_at).toLocaleString()}${order.branch_name ? ` · ${order.branch_name}` : ''}</p>
    ${channelBlock}
    <hr/>
    <table>${items}</table>
    <hr/>
    <p class="meta" style="text-align:center;font-weight:700;font-size:13px;border:1px solid #000;padding:4px;border-radius:4px;margin:6px 0">
      PAYMENT: ${formatPaymentLine(order)}
    </p>
    <p class="meta"><b>For: ${escapeHtml(order.customer_name || 'Guest')}</b></p>
    <p class="meta">Hand this slip to POS &middot; cashier verifies order ID before release</p>
    <div class="actions">
      <button onclick="window.print()">Print</button>
      <button class="secondary" onclick="window.close()">Close</button>
    </div>
    <script>
      window.addEventListener('load', function () {
        window.addEventListener('afterprint', function () { window.close(); });
        setTimeout(function () { try { window.print(); } catch (e) {} }, 120);
      });
    </script>
  </body></html>`;
}

// ---- Robust printing helpers ----------------------------------------------
// `window.open + document.write` works on desktop Chrome but is unreliable
// inside iframes (Emergent preview), inside iOS Safari PWAs (Add to Home
// Screen), and when popup blockers fire silently. So we use a *hidden
// iframe injected into the host document* and call its contentWindow.print()
// once the iframe DOM is fully loaded. We only fall back to a popup if the
// iframe approach genuinely fails.
function printHtmlReliably(html, { popupTitle = 'Print' } = {}) {
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    // sandbox would block scripts inside the printed doc; we DO want auto-print
    // to work even if the user's hand stops on the visible Print button, so
    // keep allow-modals + allow-same-origin permissions.
    document.body.appendChild(iframe);

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch (e) { /* noop */ }
    };

    const triggerPrint = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) throw new Error('iframe has no contentWindow');
        win.focus();
        // afterprint fires once the print dialog is dismissed on most browsers
        try {
          win.addEventListener('afterprint', cleanup, { once: true });
        } catch (e) { /* noop */ }
        win.print();
        // Safety-net cleanup in case afterprint never fires (some embeds)
        setTimeout(cleanup, 15000);
      } catch (e) {
        cleanup();
        throw e;
      }
    };

    // Prefer srcdoc — it provides a fully-fledged document inside the iframe
    // and reliably fires the load event after parsing is complete.
    // IMPORTANT: attach the listener BEFORE assigning srcdoc, otherwise some
    // browsers fire `load` synchronously and we miss it.
    iframe.addEventListener('load', () => {
      // Give layout one tick to settle (images, fonts, etc.) before printing.
      setTimeout(triggerPrint, 80);
    }, { once: true });
    iframe.srcdoc = html;
    return true;
  } catch (e) {
    // Fallback path — popup window. This is what the codebase used before
    // and still works on most desktop browsers when iframe printing is denied.
    try {
      const w = window.open('', '_blank', 'width=380,height=600');
      if (!w) {
        toast.error(`Pop-up blocked \u2014 allow pop-ups to ${popupTitle.toLowerCase()}`);
        return false;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      return true;
    } catch (err) {
      toast.error('Print failed. Try again or check your browser settings.');
      return false;
    }
  }
}

// Re-fetch the latest copy of the order before building the printable
// document. This eliminates the "receipt prints the wrong customer name"
// class of bug where the dialog's in-memory order is stale (e.g. user edited
// customer name after dialog opened, or another cashier changed status).
async function withFreshOrder(order) {
  if (!order?.id) return order;
  try {
    const { data } = await api.get(`/orders/${order.id}`);
    if (data && data.id === order.id) return { ...order, ...data };
  } catch (e) { /* fall back to provided order */ }
  return order;
}

export async function printKitchenTicket(order) {
  const fresh = await withFreshOrder(order);
  const html = buildKitchenTicketHtml(fresh);
  printHtmlReliably(html, { popupTitle: 'print the kitchen ticket' });
}

async function printReceipt(order) {
  const fresh = await withFreshOrder(order);
  const html = buildReceiptHtml(fresh);
  printHtmlReliably(html, { popupTitle: 'print receipts' });
}

export function OrdersQueue({ branchId }) {
  const [orders, setOrders] = useState([]);
  // Default to "Current day" so cashiers aren't crushed by thousands of
  // historical orders. Switching to "All channels" (last pill) or typing
  // into the search bar implicitly broadens the scope across all dates.
  const [channel, setChannel] = useState('today');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  // Confirmation dialog state — used for "Confirm cash received from driver"
  // and "Cancel order". Uses Shadcn AlertDialog (instead of window.confirm)
  // because native confirm() is blocked by some browsers / preview iframes
  // and rejected by sandboxed kiosk shells.
  const [confirmState, setConfirmState] = useState(null);
  const seenIds = useRef(new Set());

  // Initial + polling fallback — `branchId` is guaranteed non-empty here
  // because the parent only mounts this component when a branch is selected.
  //
  // We fetch BOTH active orders (the cashier's working queue) AND the most
  // recent cancellations (so the new "Cancelled orders" filter can surface
  // them). Cancellations are intentionally capped at 50 to keep the queue
  // snappy — anything older lives in the audit/reports surfaces.
  const fetchOrders = async () => {
    try {
      const params = { branch_id: branchId, limit: 200 };
      const [activeResp, cancelledResp, overdueResp] = await Promise.all([
        api.get('/orders', { params }),
        api.get('/orders', { params: { ...params, status: 'cancelled', limit: 50 } }),
        api.get('/orders', { params: { ...params, status: 'overdue', limit: 50 } }),
      ]);
      const active = (activeResp.data || []).filter(isQueueActive);
      const cancelled = cancelledResp.data || [];
      const overdue = overdueResp.data || [];
      // De-duplicate (shouldn't overlap but guard anyway)
      const seen = new Set(active.map((o) => o.id));
      const extras = [...cancelled, ...overdue].filter((o) => !seen.has(o.id));
      const merged = [...active, ...extras];
      // record initial IDs so we don't ding on first load
      if (seenIds.current.size === 0) {
        merged.forEach((o) => seenIds.current.add(o.id));
      }
      setOrders(merged);
    } catch (e) {
      // Silent — polling will retry
    }
  };

  // Initial fetch + interval polling fallback. The polling hook fires `fetchOrders`
  // both immediately on mount AND on each interval — keeping all state updates
  // inside a hook-controlled boundary (no direct setState-in-effect anti-pattern).
  usePolling(fetchOrders, 10000, [branchId], { immediate: true });

  // Realtime
  const { connected } = useRealtime(
    branchId ? [`admin:${branchId}`] : [],
    (msg) => {
      if (!msg?.order) return;
      const order = msg.order;
      if (order.branch_id !== branchId) return;
      if (msg.type === 'order_created') {
        setOrders((prev) => {
          if (prev.some((o) => o.id === order.id)) return prev;
          // Ding only for genuinely new orders
          if (!seenIds.current.has(order.id) && soundOn) {
            try { playAdminPing(); } catch (e) { /* noop */ }
            toast.success(`New ${order.order_type.replace('_', ' ')} order #${order.order_number}`);
          }
          seenIds.current.add(order.id);
          return [order, ...prev];
        });
      } else if (msg.type === 'order_status_changed') {
        setOrders((prev) => {
          const stillActive = isQueueActive(order);
          const isCancelled = order.status === 'cancelled';
          const exists = prev.some((o) => o.id === order.id);
          // Cancelled orders are kept in state so the new "Cancelled orders"
          // filter can surface them in real-time after a cashier cancels.
          if (!stillActive && !isCancelled) {
            return prev.filter((o) => o.id !== order.id);
          }
          if (exists) return prev.map((o) => o.id === order.id ? order : o);
          return [order, ...prev];
        });
        // refresh selected if it's the one updated
        setSelected((sel) => sel && sel.id === order.id ? order : sel);
      }
    },
  );

  // Filtering — when the cashier types into the search box AND they're on
  // the default "Current day" filter, we transparently broaden the scope to
  // "All channels" so they can find a customer's earlier-week order without
  // having to manually flip the pill. The hint banner under the toolbar
  // explains this. For any other filter (Cancelled / Overdue / specific
  // channel) we respect the cashier's deliberate scope and keep the search
  // confined to it.
  const trimmedSearch = search.trim();
  const broadenedBySearch = !!trimmedSearch && channel === 'today';
  const effectiveChannel = broadenedBySearch ? 'all' : channel;

  const filtered = useMemo(() => {
    const cfg = CHANNEL_FILTERS.find((c) => c.key === effectiveChannel) || CHANNEL_FILTERS[0];
    const s = trimmedSearch.toLowerCase();
    return orders.filter((o) => cfg.match(o) && (
      !s
      || (o.order_number || '').toLowerCase().includes(s)
      || (o.customer_name || '').toLowerCase().includes(s)
      || (o.customer_phone || '').toLowerCase().includes(s)
      || (o.table_number != null && String(o.table_number).includes(s))
    ));
  }, [orders, effectiveChannel, trimmedSearch]);

  // Counts per channel for badges. Cancelled + overdue have their own
  // buckets and are excluded from the live working queue numbers. The
  // `today`/`all` counters mirror their respective filters so the cashier
  // can see how many of the loaded orders are TODAY's vs across-time.
  const counts = useMemo(() => {
    const acc = { today: 0, all: 0, dine_in: 0, pickup: 0, delivery: 0, unpaid: 0, driver_cash: 0, cancelled: 0, overdue: 0 };
    orders.forEach((o) => {
      if (o.status === 'cancelled') { acc.cancelled += 1; return; }
      if (o.status === 'overdue') { acc.overdue += 1; return; }
      acc.all += 1;
      const today = isToday(o.created_at);
      if (today) {
        acc.today += 1;
        if (o.order_type in acc) acc[o.order_type] += 1;
        if (o.payment_status !== 'paid') acc.unpaid += 1;
        if (o.driver_cash_held && !o.driver_cash_remitted) acc.driver_cash += 1;
      }
    });
    return acc;
  }, [orders]);

  // Actions
  const refreshOne = async (id) => {
    try {
      const { data } = await api.get('/orders', { params: { branch_id: branchId, limit: 200 } });
      setOrders((data || []).filter(isQueueActive));
      const found = (data || []).find((o) => o.id === id);
      if (found) setSelected(found);
    } catch (e) { /* noop */ }
  };

  const handleMarkCashPaid = async (order) => {
    setBusy(true);
    try {
      await api.post(`/payments/cash/mark-paid/${order.id}`);
      toast.success(`Order #${order.order_number} marked paid (cash)`);
      await refreshOne(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark paid');
    } finally { setBusy(false); }
  };

  const handleMarkCardPaid = async (order) => {
    setBusy(true);
    try {
      await api.post(`/payments/card/mark-paid/${order.id}`);
      toast.success(`Order #${order.order_number} marked paid (card)`);
      await refreshOne(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark paid');
    } finally { setBusy(false); }
  };

  /**
   * Cashier-driven mobile-money payment confirmation. The customer has
   * already sent the funds to the merchant code (out-of-band); the cashier
   * verifies receipt on their phone and taps MTN or Orange here to mark the
   * order paid. We call initiate + confirm back-to-back so a single tap
   * records the transaction and flips payment_status to 'paid'.
   *
   * provider: 'mtn_money' | 'orange_money'
   */
  const handleMarkMobileMoneyPaid = async (order, provider) => {
    if (!['mtn_money', 'orange_money'].includes(provider)) return;
    setBusy(true);
    try {
      await api.post(`/payments/mobile-money/initiate/${order.id}`, null, { params: { provider } });
      await api.post(`/payments/mobile-money/confirm/${order.id}`);
      const label = provider === 'mtn_money' ? 'MTN Money' : 'Orange Money';
      toast.success(`Order #${order.order_number} marked paid (${label})`);
      await refreshOne(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record mobile money payment');
    } finally { setBusy(false); }
  };

  const handleCancel = (order) => {
    setConfirmState({
      kind: 'cancel',
      order,
      title: `Cancel order #${order.order_number}?`,
      description: 'This will mark the order as cancelled. If the customer already paid, you will need to issue a refund manually. This cannot be undone.',
      confirmLabel: 'Yes, cancel order',
      destructive: true,
    });
  };

  const doCancel = async (order) => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/cancel`);
      toast.success(`Order #${order.order_number} cancelled`);
      setSelected(null);
      await refreshOne(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    } finally { setBusy(false); }
  };

  // Mark no-show — pickup customer never came in, or delivery customer
  // not reachable. Backend cancels the order with the right reason and
  // increments the strike counter (auto-banning at the policy threshold).
  const handleMarkNoShow = (order) => {
    const isPickup = order.order_type === 'pickup';
    setConfirmState({
      kind: 'no_show',
      order,
      title: isPickup
        ? `Mark pickup #${order.order_number} as no-show?`
        : `Mark delivery #${order.order_number} as no-show?`,
      description: (
        (isPickup
          ? 'The customer never came in to pick up this order. The order will be cancelled and the customer will receive a strike on their account.'
          : 'The customer was not reachable at the door. The order will be cancelled and the customer will receive a strike on their account.')
        + '\n\nReaching the strike threshold will auto-ban the customer\u2019s phone number from placing future orders.'
      ),
      confirmLabel: 'Yes, mark no-show',
      destructive: true,
    });
  };

  const doMarkNoShow = async (order) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/mark-no-show`, {});
      if (data?.banned_now) {
        toast.warning(
          `Order #${order.order_number} marked no-show. Customer ${order.customer_phone || ''} hit the strike threshold and has been auto-banned.`,
        );
      } else {
        toast.success(`Order #${order.order_number} marked no-show.`);
      }
      setSelected(null);
      await refreshOne(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark no-show');
    } finally { setBusy(false); }
  };

  // Cashier confirms physical cash handed over by the driver.
  // Sends SMS receipt to the driver as proof — closes the audit loop so the
  // cashier can never claim later "I didn't receive the money".
  const handleDriverRemit = (order) => {
    setConfirmState({
      kind: 'remit',
      order,
      title: 'Confirm cash receipt from driver',
      description: (
        `Order #${order.order_number} — amount $${(order.total || 0).toFixed(2)}.\n\n`
        + 'An SMS receipt will be sent to the driver as proof that the cash has been handed over to the cashier.'
      ),
      confirmLabel: 'Confirm — cash received',
      destructive: false,
    });
  };

  const doDriverRemit = async (order) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery/${order.id}/driver-cash-remit`);
      toast.success(
        `Cash received from driver — receipt ${data.driver_cash_remit_receipt}. SMS sent.`,
      );
      await refreshOne(order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record remit');
    } finally { setBusy(false); }
  };

  const onConfirmDialogAccept = () => {
    if (!confirmState) return;
    const { kind, order } = confirmState;
    setConfirmState(null);
    if (kind === 'cancel') doCancel(order);
    else if (kind === 'remit') doDriverRemit(order);
    else if (kind === 'no_show') doMarkNoShow(order);
  };

  const toggleSound = async () => {
    if (!soundOn) {
      if (!isAudioUnlocked()) await unlockAudio();
      try { playAdminPing(); } catch (e) { /* noop */ }
    }
    setSoundOn((v) => !v);
  };

  return (
    <div className="flex h-full min-h-[70vh] flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card px-3 py-2">
        <div className="relative w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="orders-queue-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search #order, name, table…"
            className="pl-9"
          />
        </div>
        {CHANNEL_FILTERS.map((f) => (
          <Button
            key={f.key}
            data-testid={`orders-filter-${f.key}`}
            size="sm"
            variant={effectiveChannel === f.key ? 'default' : 'secondary'}
            onClick={() => setChannel(f.key)}
            className="gap-1.5"
          >
            {f.label}
            {counts[f.key] > 0 && (
              <Badge variant="outline" className="ml-0.5 h-5 min-w-5 border-0 bg-background/30 px-1.5 text-[10px]">
                {counts[f.key]}
              </Badge>
            )}
          </Button>
        ))}
        {broadenedBySearch && (
          <span
            data-testid="orders-queue-search-broadened-hint"
            className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
          >
            <Search className="h-3 w-3" /> Searching across all dates
          </span>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {connected ? (
            <span className="inline-flex items-center gap-1 text-emerald-600"><Wifi className="h-3.5 w-3.5" /> Live</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-600"><WifiOff className="h-3.5 w-3.5" /> Polling</span>
          )}
          <Button
            data-testid="orders-toggle-sound"
            size="sm"
            variant="ghost"
            onClick={toggleSound}
            title={soundOn ? 'Mute new-order chime' : 'Enable new-order chime'}
          >
            {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
          </Button>
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        <div data-testid="orders-queue-list" className="grid gap-3 p-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center gap-2 py-16 text-center text-muted-foreground">
              <Clock className="h-8 w-8 opacity-40" />
              <p className="text-sm">No active orders right now.</p>
              <p className="text-xs">New iPad / online / walk-in orders will appear here instantly.</p>
            </div>
          )}
          {filtered.map((o) => {
            const isPaid = o.payment_status === 'paid';
            const isCancelled = o.status === 'cancelled';
            const isOverdue = o.status === 'overdue';
            const typeBadge = ORDER_TYPE_BADGE[o.order_type] || { label: o.order_type, cls: '' };
            const driverHoldingCash = o.driver_cash_held && !o.driver_cash_remitted;
            return (
              <Card
                key={o.id}
                data-testid={isCancelled ? 'order-card-cancelled' : isOverdue ? 'order-card-overdue' : 'order-card'}
                className={
                  `group flex h-full flex-col overflow-hidden border-border transition-all hover:border-primary hover:shadow-md `
                  + (driverHoldingCash ? 'ring-2 ring-amber-400 ' : '')
                  + (isCancelled ? 'opacity-90 ring-2 ring-rose-400 bg-rose-50/40 ' : '')
                  + (isOverdue ? 'ring-2 ring-amber-500 bg-amber-50/60 shadow-[0_0_0_4px_rgba(245,158,11,0.08)] ' : '')
                }
              >
                <button
                  type="button"
                  data-testid="order-card-open"
                  onClick={() => setSelected(o)}
                  className="flex w-full items-start justify-between gap-2 border-b border-border bg-secondary/40 px-3 py-2 text-left transition-colors hover:bg-secondary"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-display text-sm font-bold tracking-tight ${isCancelled ? 'line-through decoration-2 text-rose-700' : ''} ${isOverdue ? 'text-amber-800' : ''}`}>#{o.order_number}</p>
                      <Badge className={`border text-[10px] ${typeBadge.cls}`}>{typeBadge.label}</Badge>
                      {isOverdue && (
                        <Badge data-testid="card-overdue-badge" className="animate-pulse border border-amber-500 bg-amber-100 text-[10px] font-bold uppercase tracking-wider text-amber-900">
                          <AlertTriangle className="mr-0.5 h-3 w-3" /> Overdue
                        </Badge>
                      )}
                      {isCancelled && (
                        <Badge variant="outline" className="border-rose-400 bg-rose-100 text-[10px] font-semibold text-rose-800">
                          <Ban className="mr-0.5 h-3 w-3" /> CANCELLED
                        </Badge>
                      )}
                      {!isCancelled && !isOverdue && !isPaid && <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700">UNPAID</Badge>}
                      {!isCancelled && !isOverdue && driverHoldingCash && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                          <HandCoins className="mr-0.5 h-3 w-3" /> CASH W/ DRIVER
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {o.customer_name || 'Guest'} &middot; {fmtTimeAgo(o.created_at)}
                      {isCancelled && o.cancel_reason ? ` \u00b7 ${String(o.cancel_reason).replace(/_/g, ' ')}` : ''}
                      {isOverdue ? ' \u00b7 crossed kitchen day reset' : ''}
                    </p>
                  </div>
                  <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </button>

                <CardContent className="flex flex-1 flex-col gap-2 p-3">
                  {driverHoldingCash && (
                    <div data-testid="card-driver-cash-banner" className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs">
                      <p className="flex items-center gap-1 font-semibold text-amber-900">
                        <HandCoins className="h-3.5 w-3.5" />
                        Driver is holding {fmtMoney(o.total, o.currency)} cash
                      </p>
                      <p className="mt-0.5 text-[11px] text-amber-800">
                        Collected {o.driver_cash_collected_at ? fmtTimeAgo(o.driver_cash_collected_at) : 'just now'}. Confirm when handed over.
                      </p>
                      <Button
                        data-testid="card-driver-cash-remit"
                        size="sm"
                        className="mt-2 h-8 w-full bg-amber-600 px-2 text-xs hover:bg-amber-700"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); handleDriverRemit(o); }}
                      >
                        <HandCoins className="mr-1 h-3.5 w-3.5" /> Confirm cash received from driver
                      </Button>
                    </div>
                  )}
                  {/* Channel-specific row */}
                  <div className="flex items-center gap-1.5 text-xs">
                    {o.order_type === 'dine_in' && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 font-medium text-blue-900">
                        <Store className="h-3 w-3" /> Table {o.table_number ?? '—'}
                      </span>
                    )}
                    {o.order_type === 'pickup' && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2 py-0.5 font-medium text-purple-900">
                        <Store className="h-3 w-3" /> Counter pickup
                      </span>
                    )}
                    {o.order_type === 'delivery' && (
                      <span className="inline-flex max-w-full items-center gap-1 truncate rounded-md bg-amber-50 px-2 py-0.5 font-medium text-amber-900">
                        <MapPin className="h-3 w-3" />
                        {o.delivery_address?.city || o.delivery_address?.line1 || 'Delivery'}
                      </span>
                    )}
                    {o.customer_phone && (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Phone className="h-3 w-3" /> {o.customer_phone}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <Badge className={`w-full justify-center border text-[10px] ${ORDER_STATUS_COLORS[o.status] || ''}`}>
                      {getContextualStatusLabel(o.status, o.order_type, o.table_number)}
                    </Badge>
                  </div>

                  {/* Items preview */}
                  <ul className="space-y-0.5 text-xs">
                    {(o.items || []).slice(0, 3).map((it, idx) => (
                      <li key={idx} className="flex justify-between gap-2">
                        <span className="truncate">{it.quantity}× {it.name}</span>
                      </li>
                    ))}
                    {(o.items?.length || 0) > 3 && (
                      <li className="text-muted-foreground">+ {o.items.length - 3} more…</li>
                    )}
                  </ul>

                  <div className="mt-auto flex items-center justify-between pt-1">
                    <span className="text-xs text-muted-foreground">Total</span>
                    <span className="tabular text-sm font-semibold">{fmtMoney(o.total, o.currency)}</span>
                  </div>

                  {/* Quick actions — POS cashiers ONLY process payment. The
                      cashier card intentionally does NOT expose the kitchen
                      status advance ("Start cooking / Ready / Served") —
                      that belongs to the KDS where the kitchen staff drive
                      the food workflow. Three payment methods are surfaced:
                      Cash, Mobile Money (MTN / Orange popover), and Card.
                      Cancelled & overdue orders only offer the receipt
                      reprint action. */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {!isCancelled && !isOverdue && !isPaid && (
                      <Button
                        data-testid="card-mark-cash-paid"
                        size="sm"
                        className="h-7 flex-1 px-2 text-xs"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); handleMarkCashPaid(o); }}
                      >
                        <Wallet className="mr-1 h-3 w-3" /> Cash
                      </Button>
                    )}
                    {!isCancelled && !isOverdue && !isPaid && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            data-testid="card-mark-mobile-money-trigger"
                            size="sm"
                            variant="secondary"
                            className="h-7 flex-1 px-2 text-xs"
                            disabled={busy}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Smartphone className="mr-1 h-3 w-3" /> Mobile
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            data-testid="card-mark-mtn-money-paid"
                            onClick={(e) => { e.stopPropagation(); handleMarkMobileMoneyPaid(o, 'mtn_money'); }}
                          >
                            <Smartphone className="mr-2 h-3.5 w-3.5 text-amber-600" /> MTN Money
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid="card-mark-orange-money-paid"
                            onClick={(e) => { e.stopPropagation(); handleMarkMobileMoneyPaid(o, 'orange_money'); }}
                          >
                            <Smartphone className="mr-2 h-3.5 w-3.5 text-orange-600" /> Orange Money
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {!isCancelled && !isOverdue && !isPaid && (
                      <Button
                        data-testid="card-mark-card-paid"
                        size="sm"
                        variant="secondary"
                        className="h-7 flex-1 px-2 text-xs"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); handleMarkCardPaid(o); }}
                      >
                        <CreditCard className="mr-1 h-3 w-3" /> Card
                      </Button>
                    )}
                    <Button
                      data-testid="card-print-receipt"
                      size="sm"
                      variant={(isCancelled || isOverdue) ? 'secondary' : 'ghost'}
                      className={`h-7 px-2 text-xs ${(isCancelled || isOverdue) ? 'flex-1' : ''}`}
                      onClick={(e) => { e.stopPropagation(); printReceipt(o); }}
                      title="Print receipt"
                    >
                      <Printer className={(isCancelled || isOverdue) ? 'mr-1 h-3 w-3' : 'h-3 w-3'} />
                      {(isCancelled || isOverdue) ? 'Print receipt' : null}
                    </Button>
                    {!isCancelled && !isOverdue && (
                      <Button
                        data-testid="card-cancel-order"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); handleCancel(o); }}
                        title="Cancel order"
                      >
                        <Ban className="h-3 w-3" />
                      </Button>
                    )}
                    {!isCancelled && !isOverdue && (o.order_type === 'pickup' || o.order_type === 'delivery')
                      && !['completed', 'delivered', 'cancelled'].includes(o.status) && (
                      <Button
                        data-testid="card-mark-no-show"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); handleMarkNoShow(o); }}
                        title={o.order_type === 'pickup' ? 'Customer never came in (no-show)' : 'Customer unreachable at delivery (no-show)'}
                      >
                        <UserX className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      <OrderDetailDialog
        order={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onMarkCashPaid={handleMarkCashPaid}
        onMarkCardPaid={handleMarkCardPaid}
        onMarkMobileMoneyPaid={handleMarkMobileMoneyPaid}
        onCancel={handleCancel}
        onMarkNoShow={handleMarkNoShow}
        onPrintReceipt={printReceipt}
        onDriverRemit={handleDriverRemit}
        busy={busy}
      />

      <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) setConfirmState(null); }}>
        <AlertDialogContent data-testid="pos-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {confirmState?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pos-confirm-cancel">Back</AlertDialogCancel>
            <AlertDialogAction
              data-testid="pos-confirm-accept"
              onClick={onConfirmDialogAccept}
              className={confirmState?.destructive ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-amber-600 text-white hover:bg-amber-700'}
            >
              {confirmState?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
